import React, {
    ReactElement, useEffect, useState,
} from "react";
import { Form } from "react-bootstrap";
import {
    ChannelMode,
    ChatSettingsMicrosoftTeams, ChatSettingsMicrosoftTeamsInput,
} from "../../../../../__generated__/api";
import { isDeepEqual } from "../../../../../api";
import ChannelTags from "../../../../../components/pickers/channel-tags/ChannelTags";
import useRouting, { Link } from "../../../../../contexts/Routing";
import ChannelModeHelp from "../../channels/slack/ChannelModeHelp";
import Select from "../../../../../components/select/Select";

export interface Props {
    data: ChatSettingsMicrosoftTeams | undefined,
    onChange: (options: ChatSettingsMicrosoftTeamsInput) => void;
    canAddTags: boolean;
}

const CHANNEL_MODES = {
    [ChannelMode.Threaded]: "Threaded",
    [ChannelMode.Manual]: "Manual",
};

export default function MicrosoftTeamsSettings({
    data, onChange, canAddTags,
}: Props): ReactElement {
    const { Paths } = useRouting();

    const [tags, setTags] = useState(() => data?.tags ?? []);
    const [channelMode, setChannelMode] = useState(() => data?.channelMode);

    useEffect(() => {
        if (!data) {
            return;
        }
        setTags(data?.tags ?? []);
        setChannelMode(data?.channelMode);
    }, [data]);

    useEffect(() => {
        const updated = {
            tags: tags ?? [],
            name: data?.name,
            channelMode,
        };
        const areEqual = isDeepEqual(updated, data, []);
        if (!areEqual) {
            onChange(updated);
        }
    }, [tags, channelMode]);

    return (
        <div className="border p-3 rounded bg-white mt-4 mb-4">
            <h5 className="mb-3 text-light">
                Microsoft Teams
                {" "}
                {data?.workspaceName && (
                    <small className="me-2 badge bg-light text-white">
                        {data?.workspaceName}
                    </small>
                )}

                {data?.name ? `(#${data.name})` : ""}
            </h5>

            {canAddTags && (
                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder">
                        Channel Tags
                    </Form.Label>
                    <ChannelTags
                        placeholder="Please pick a list of channel tags"
                        isMulti
                        onSelected={selected => setTags(selected)}
                        value={tags}
                        canCreate
                    />
                    <Form.Text>
                        Which tags should be associated with this queue. To manage channel tags, visit
                        {" "}
                        <Link
                            to={Paths.ADMINISTRATOR_SETTING}
                            params={{ setting: "channel-tags" }}
                            className="link-info text-decoration-none"
                        >
                            General Settings
                        </Link>
                        .
                    </Form.Text>
                </Form.Group>
            )}

            <Form.Group className="mb-3">
                <Form.Label className="fw-bolder">
                    Channel Mode
                    {" "}
                    <ChannelModeHelp />
                </Form.Label>

                <Select
                    className="text-gray"
                    placeholder=""
                    isClearable
                    onChange={value => setChannelMode(value?.value as ChannelMode)}
                    options={Object.entries(CHANNEL_MODES).map(([value, label]) => ({ value, label }))}
                    value={!channelMode ? null : {
                        value: channelMode,
                        label: CHANNEL_MODES[channelMode as keyof typeof CHANNEL_MODES],
                    }}
                />
                <Form.Text>
                    How do you when tickets to be created in this Slack channel?
                    <ChannelModeHelp buttonClassName="m-0 ms-1" />
                </Form.Text>
            </Form.Group>

        </div>
    );
}

MicrosoftTeamsSettings.defaultProps = {
};
